// Tiny data-fetching hook: runs an async loader, tracks loading/error/data,
// and exposes a reload(). Used by views to pull REST resources. Errors are
// captured (never thrown into render) so a failed fetch shows a message, not a
// blank crash.

import { useCallback, useEffect, useRef, useState } from 'react';

export interface Resource<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  // reload(true) = QUIET background refresh: keeps `data` + `loading=false` so the view doesn't flash
  // its loading state (used by live/polling views). reload() / reload(false) = normal (shows loading).
  reload: (quiet?: boolean) => void;
}

export function useResource<T>(loader: () => Promise<T>, deps: unknown[] = []): Resource<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const quietRef = useRef(false);

  const run = useCallback(() => {
    let cancelled = false;
    if (!quietRef.current) setLoading(true);
    loader()
      .then((d) => {
        if (!cancelled) {
          setData(d);
          setError(null);
        }
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
        quietRef.current = false;
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nonce, ...deps]);

  useEffect(run, [run]);

  return {
    data,
    loading,
    error,
    reload: (quiet = false) => {
      quietRef.current = quiet;
      setNonce((n) => n + 1);
    },
  };
}
